import { GetBackAttivitaV2IdComunicazioniIdTipoDestinatario } from '@framework/api-client/api/backoffice/schemas/getBackAttivitaV2IdComunicazioniIdTipoDestinatario.ts';
import { GetBackAttivitaV2IdComunicazioniIdTipoMittente } from '@framework/api-client/api/backoffice/schemas/getBackAttivitaV2IdComunicazioniIdTipoMittente.ts';
import {
  type SearchableField,
  SearchableFieldGroup,
  SearchableFieldType,
} from '@framework/ui/components/search/ISearchableField.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CommunicationSubprocedureSelectInput } from '../../form/office/CommunicationSubprocedureSelectInput.tsx';
import { CommunicationSearchableTypeSelectInput } from '../CommunicationSearchableTypeSelectInput.tsx';

export function useCommunicationSearchableFields(options: {
  isBackoffice: boolean;
}) {
  const { isBackoffice } = options;
  const { t } = useTranslation();

  const searchableFields: SearchableField[] = useMemo(
    () =>
      [
        {
          type: SearchableFieldType.Text,
          name: 'messaggio',
          label: t('communication-extend-search-form.message', 'Messaggio'),
          group: SearchableFieldGroup.Filters,
          placeholder: t(
            'communication-extend-search-form.search-in-message',
            'Cerca nel messaggio...',
          ),
        },
        {
          type: SearchableFieldType.DateRange,
          name: 'dataInvio',
          label: t('communication-extend-search-form.sent', 'Inviata il'),
          group: SearchableFieldGroup.Filters,
          from: {
            label: t(
              'communication-extend-search-form.sent-after',
              'Inviata dopo il',
            ),
            name: 'dataInvioDal',
            placeholder: t(
              'communication-extend-search-form.write-or-select-date',
              'Scrivi o seleziona la data...',
            ),
          },
          to: {
            label: t(
              'communication-extend-search-form.sent-before',
              'Inviata prima del',
            ),
            name: 'dataInvioAl',
            placeholder: t(
              'communication-extend-search-form.write-or-select-date',
              'Scrivi o seleziona la data...',
            ),
          },
        },
        {
          type: SearchableFieldType.Custom,
          group: SearchableFieldGroup.Filters,
          name: 'idTipoComunicazione',
          label: t(
            'communication-extend-search-form.communication-type',
            'Tipo Comunicazione',
          ),
          input: (
            <CommunicationSearchableTypeSelectInput
              name="idTipoComunicazione"
              allowClear
              size="middle"
            />
          ),
        },
        {
          type: SearchableFieldType.Select,
          group: SearchableFieldGroup.Filters,
          name: 'idTipoMittente',
          label: t(
            'communication-extend-search-form.sender-type',
            'Tipo Mittente',
          ),
          options: [
            {
              label: t(
                'communication-extend-search-form.sender-type-titolare',
                'Titolare',
              ),
              value: GetBackAttivitaV2IdComunicazioniIdTipoMittente.TITOLARE,
            },
            {
              label: t(
                'communication-extend-search-form.sender-type-richiedente',
                'Richiedente',
              ),
              value: GetBackAttivitaV2IdComunicazioniIdTipoMittente.RICHIEDENTE,
            },
            {
              label: t(
                'communication-extend-search-form.sender-type-responsabile-procedimento',
                'Responsabile Procedimento',
              ),
              value:
                GetBackAttivitaV2IdComunicazioniIdTipoMittente.RESPONSABILE_PROCEDIMENTO,
            },
            ...(isBackoffice
              ? [
                  {
                    label: t(
                      'communication-extend-search-form.sender-type-endoprocedimento-primo-livello',
                      'Endoprocedimento Primo Livello',
                    ),
                    value:
                      GetBackAttivitaV2IdComunicazioniIdTipoMittente.ENDOPROCEDIMENTI_PRIMO_LIVELLO,
                  },
                ]
              : []),
            {
              label: t(
                'communication-extend-search-form.sender-type-altro-ufficio',
                'Altro ufficio',
              ),
              value:
                GetBackAttivitaV2IdComunicazioniIdTipoMittente.ALTRO_UFFICIO_VARIABILE,
            },
          ],
        },
        {
          type: SearchableFieldType.Select,
          group: SearchableFieldGroup.Filters,
          name: 'idTipoDestinatario',
          label: t(
            'communication-extend-search-form.recipient-type',
            'Tipo Destinatario',
          ),
          options: [
            {
              label: t(
                'communication-extend-search-form.recipient-type-titolare',
                'Titolare',
              ),
              value:
                GetBackAttivitaV2IdComunicazioniIdTipoDestinatario.TITOLARE,
            },
            {
              label: t(
                'communication-extend-search-form.recipient-type-richiedente',
                'Richiedente',
              ),
              value:
                GetBackAttivitaV2IdComunicazioniIdTipoDestinatario.RICHIEDENTE,
            },
            {
              label: t(
                'communication-extend-search-form.recipient-type-responsabile-procedimento',
                'Responsabile Procedimento',
              ),
              value:
                GetBackAttivitaV2IdComunicazioniIdTipoDestinatario.RESPONSABILE_PROCEDIMENTO,
            },
            {
              label: t(
                'communication-extend-search-form.recipient-type-procuratore',
                'Procuratore',
              ),
              value:
                GetBackAttivitaV2IdComunicazioniIdTipoDestinatario.PROCURATORE,
            },
            {
              label: t(
                'communication-extend-search-form.recipient-type-impresa-titolare',
                'Impresa Titolare',
              ),
              value:
                GetBackAttivitaV2IdComunicazioniIdTipoDestinatario.IMPRESA_TITOLARE,
            },
            ...(isBackoffice
              ? [
                  {
                    label: t(
                      'communication-extend-search-form.recipient-type-endoprocedimento-primo-livello',
                      'Endoprocedimento Primo Livello',
                    ),
                    value:
                      GetBackAttivitaV2IdComunicazioniIdTipoDestinatario.ENDOPROCEDIMENTI_PRIMO_LIVELLO,
                  },
                ]
              : []),
            {
              label: t(
                'communication-extend-search-form.recipient-type-delegati-firma',
                'Delegati alla Firma',
              ),
              value:
                GetBackAttivitaV2IdComunicazioniIdTipoDestinatario.DELEGATI_FIRMA,
            },
            {
              label: t(
                'communication-extend-search-form.recipient-type-delegati-compilazione',
                'Delegati alla Compilazione',
              ),
              value:
                GetBackAttivitaV2IdComunicazioniIdTipoDestinatario.DELEGATI_COMPILAZIONE,
            },
            {
              label: t(
                'communication-extend-search-form.recipient-type-altro-ufficio',
                'Altro ufficio',
              ),
              value:
                GetBackAttivitaV2IdComunicazioniIdTipoDestinatario.ALTRO_UFFICIO_VARIABILE,
            },
          ],
        },
        {
          type: SearchableFieldType.Select,
          group: SearchableFieldGroup.Filters,
          name: 'flLetta',
          label: t('communication-extend-search-form.read-state', 'Lettura'),
          renderValue: (value: unknown) =>
            value === false || value === 'false'
              ? t(
                  'communication-extend-search-form.read-state-unread',
                  'Non lette',
                )
              : t('communication-extend-search-form.read-state-read', 'Lette'),
          options: [
            {
              label: t(
                'communication-extend-search-form.read-state-unread',
                'Non lette',
              ),
              value: false,
            },
            {
              label: t(
                'communication-extend-search-form.read-state-read',
                'Lette',
              ),
              value: true,
            },
          ],
        },
        {
          type: SearchableFieldType.Text,
          name: 'mittente',
          placeholder: t(
            'communication-extend-search-form.search-sender',
            'Nominativo mittente...',
          ),
          label: t('communication-extend-search-form.sender', 'Mittente'),
          group: SearchableFieldGroup.Filters,
        },
        {
          type: SearchableFieldType.Text,
          name: 'destinatario',
          placeholder: t(
            'communication-extend-search-form.search-recipient',
            'Nominativo destinatario...',
          ),
          label: t(
            'communication-extend-search-form.recipient',
            'Destinatario',
          ),
          group: SearchableFieldGroup.Filters,
        },
        ...(isBackoffice
          ? [
              {
                type: SearchableFieldType.Custom,
                group: SearchableFieldGroup.Filters,
                name: 'idEndoprocedimento',
                label: t(
                  'communication-extend-search-form.subprocedure',
                  'Endoprocedimento',
                ),
                input: (
                  <CommunicationSubprocedureSelectInput
                    name={'idEndoprocedimento'}
                    placeholder={t(
                      'communications.select-receiver-subprocedure',
                      'Seleziona endoprocedimento destinatario',
                    )}
                  />
                ),
              },
            ]
          : []),
      ].sort((a, b) => a.label.localeCompare(b.label)) as SearchableField[],
    [isBackoffice, t],
  );

  return searchableFields;
}
